import React, { useLayoutEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight } from 'lucide-react';
import { gsap } from 'gsap';
import { projects } from '../../data/projects';
import { preparer } from '../../lib/transitionProjet';
import '../../styles/work.css';

const WorkIndex = () => {
  const [active, setActive] = useState(null);
  const previewRef = useRef(null);
  const suivreX = useRef(null);
  const suivreY = useRef(null);

  // L'aperçu suit le curseur avec de l'inertie plutôt que de lui coller au
  // pixel : il a une masse, et la liste devient un instrument. quickTo garde
  // un seul tween réutilisé, au lieu d'en créer un à chaque mouvement.
  useLayoutEffect(() => {
    const el = previewRef.current;
    if (!el) return undefined;

    gsap.set(el, { xPercent: -50, yPercent: -50 });

    // En mouvement réduit l'aperçu suit quand même, mais sans traîne : couper
    // le suivi le laisserait bloqué en haut à gauche de l'écran. quickTo avec
    // une durée nulle ne pose pas la valeur, d'où l'écriture directe.
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      suivreX.current = (v) => gsap.set(el, { x: v });
      suivreY.current = (v) => gsap.set(el, { y: v });
    } else {
      suivreX.current = gsap.quickTo(el, 'x', { duration: 0.5, ease: 'power3' });
      suivreY.current = gsap.quickTo(el, 'y', { duration: 0.5, ease: 'power3' });
    }

    return () => {
      suivreX.current = null;
      suivreY.current = null;
      gsap.killTweensOf(el);
    };
  }, []);

  const onMove = (e) => {
    suivreX.current?.(e.clientX);
    suivreY.current?.(e.clientY);
  };

  return (
    <section className="section work" id="work" onMouseMove={onMove}>
      <div className="shell">
        <div className="section-head">
          <h2>Six projets, du wireframe à la mise en ligne</h2>
        </div>

        <ul className="work-list">
          {projects.map((project, index) => (
            <li
              key={project.slug}
              className={`work-row ${
                active && active.slug !== project.slug ? 'is-recule' : ''
              }`}
              onMouseEnter={() => setActive(project)}
              onMouseLeave={() => setActive(null)}
            >
              <Link
                to={`/projets/${project.slug}`}
                className="work-link"
                onClick={() => {
                  // L'aperçu n'existe qu'au survol : au clavier ou au doigt,
                  // il n'y a rien à faire voler et la navigation reste normale.
                  if (active?.slug === project.slug && previewRef.current) {
                    preparer(project.image, previewRef.current.getBoundingClientRect());
                  }
                }}
              >
                {/* Sans survol — téléphone, tablette — l'aperçu au curseur
                    n'existe pas et la liste ne montrerait aucun visuel. */}
                <img
                  className="work-vignette"
                  src={project.image}
                  alt=""
                  loading="lazy"
                  decoding="async"
                />

                <span className="work-index" aria-hidden="true">
                  {String(index + 1).padStart(2, '0')}
                </span>

                <span className="work-name">{project.title}</span>

                <span className="work-tail">
                  <span className="work-subtitle">{project.subtitle}</span>
                  <span className="work-year">{project.year}</span>
                  <ArrowUpRight className="work-arrow" size={22} aria-hidden="true" />
                </span>
              </Link>
            </li>
          ))}
        </ul>
      </div>

      {/* Aperçu qui suit le curseur */}
      <div
        className={`work-preview ${active ? 'is-visible' : ''}`}
        ref={previewRef}
        aria-hidden="true"
      >
        {active && <img src={active.image} alt="" loading="lazy" />}
      </div>
    </section>
  );
};

export default WorkIndex;
